import React, { useMemo, useState } from "react";
import { fmtUSD } from "../lib/format";

/**
 * Lightweight SVG bar chart of daily profit.
 * - Days on the X-axis, profit on the Y-axis (zero baseline in the middle).
 * - Green for positive, rose for negative, zinc-300 for null/unknown.
 * - Hovering a bar reveals a tooltip with date + profit.
 */
export default function ProfitSparkline({ days = [] }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  const { points, maxAbs, totalProfit } = useMemo(() => {
    const pts = days.map((d, i) => ({
      i,
      date: d.date,
      profit: d.profit_usd,
      isToday: d.is_today,
    }));
    const known = pts.filter((p) => p.profit != null).map((p) => p.profit);
    const m = known.length ? Math.max(1, ...known.map((v) => Math.abs(v))) : 1;
    const total = known.reduce((s, v) => s + v, 0);
    return { points: pts, maxAbs: m, totalProfit: total };
  }, [days]);

  if (!days.length) return null;

  const W = 760;
  const H = 160;
  const padX = 20;
  const padY = 18;
  const innerW = W - padX * 2;
  const innerH = H - padY * 2;
  const zeroY = padY + innerH / 2;
  const barGap = 2;
  const barW = Math.max(2, innerW / points.length - barGap);

  const hovered = hoverIdx != null ? points[hoverIdx] : null;

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11.5px] uppercase tracking-widest text-zinc-500">
          Profit · last {days.length} days
        </div>
        <div className="text-[11.5px] font-mono">
          <span className="text-zinc-500 mr-1">Σ</span>
          <span
            className={
              totalProfit >= 0 ? "text-emerald-700" : "text-rose-700"
            }
          >
            {fmtUSD(totalProfit)}
          </span>
        </div>
      </div>
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          preserveAspectRatio="none"
          data-testid="profit-sparkline"
        >
          {/* zero line */}
          <line
            x1={padX}
            x2={W - padX}
            y1={zeroY}
            y2={zeroY}
            stroke="#e4e4e7"
            strokeWidth="1"
          />
          {/* bars */}
          {points.map((p, idx) => {
            const x = padX + idx * (barW + barGap);
            if (p.profit == null) {
              return (
                <rect
                  key={p.date}
                  x={x}
                  y={zeroY - 1}
                  width={barW}
                  height={2}
                  fill="#d4d4d8"
                  onMouseEnter={() => setHoverIdx(idx)}
                  onMouseLeave={() => setHoverIdx(null)}
                />
              );
            }
            const h = (Math.abs(p.profit) / maxAbs) * (innerH / 2);
            const y = p.profit >= 0 ? zeroY - h : zeroY;
            const fill =
              p.profit >= 0
                ? hoverIdx === idx
                  ? "#047857"
                  : "#10b981"
                : hoverIdx === idx
                ? "#be123c"
                : "#f43f5e";
            return (
              <g key={p.date}>
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(1, h)}
                  fill={fill}
                  rx="1"
                  onMouseEnter={() => setHoverIdx(idx)}
                  onMouseLeave={() => setHoverIdx(null)}
                />
                {p.isToday && (
                  <circle
                    cx={x + barW / 2}
                    cy={padY - 4}
                    r="2.5"
                    fill="#f59e0b"
                  />
                )}
              </g>
            );
          })}
        </svg>
        {hovered && (
          <div
            className="absolute pointer-events-none rounded border hairline bg-white shadow-sm px-2 py-1 text-[11px] font-mono"
            style={{
              left: `${(padX + hoverIdx * (barW + barGap) + barW / 2) /
                W *
                100}%`,
              top: -2,
              transform: "translate(-50%, -100%)",
              whiteSpace: "nowrap",
            }}
          >
            <div className="text-zinc-500">{hovered.date}</div>
            <div
              className={
                hovered.profit == null
                  ? "text-zinc-400"
                  : hovered.profit >= 0
                  ? "text-emerald-700"
                  : "text-rose-700"
              }
            >
              {hovered.profit == null ? "no data" : fmtUSD(hovered.profit)}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-1 text-[10.5px] font-mono text-zinc-500">
        <span>{points[0]?.date}</span>
        <span className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-emerald-500" />
            profit
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm bg-rose-500" />
            loss
          </span>
        </span>
        <span>{points[points.length - 1]?.date}</span>
      </div>
    </div>
  );
}
